import { usePageModels } from '@/context/PageModels';
import { AssetDownloadWrapper, Chip, PartnerOnly, RichText } from '@/features/shared';
import CardDownloadButton from '@/features/shared/ContentCard/CardDownloadButton/CardDownloadButton';
import ElasticComponentContext from '@/features/shared/ElasticComponent/ElasticComponentContext';
import { useCardCategories } from '@/features/shared/Hooks/categories/useCardCategories';
import useDownloadResultContent from '@/features/shared/Hooks/useDownloadResultContent';
import { MuiButtonBase, MuiCard, MuiHidden, MuiTypography } from '@/features/shared/material-ui';
import {
  getPageUrl,
  mapColorNameToContrastColorName,
  mapLinkToProps,
  matchExternalLink,
} from '@/utils/helpers';
import { BrPageContext } from '@bloomreach/react-sdk';
import clsx from 'clsx';
import { prop } from 'lodash/fp';
import React, {
  memo,
  MouseEvent,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import { CardResultProps } from './Card.interface';
import useStyles from './Card.styles';

const CardResult = ({
  tags,
  image,
  title,
  description,
  theme,
  date,
  url,
  category,
  categoryType,
  cardColor,
  labels,
  categoryUrl = {},
  contentCategory = '',
  isPartnerOnly,
  isDownloadable,
}: PropsWithChildren<CardResultProps>) => {
  const contentRef = React.useRef<HTMLDivElement>(null);
  const page = useContext(BrPageContext);
  const chipColor = useMemo(
    () => mapColorNameToContrastColorName({ colorName: cardColor, darker: true }),
    [cardColor],
  );
  const classes = useStyles({});
  const HAS_IMAGE = !!image;
  const MAX_LENGTH_DESCRIPTION = 300;
  const hasCategory = (category && category.length > 0) || contentCategory;
  const HREF = useMemo(() => page && getPageUrl({ url: url || '', page }), [url, page]);
  const { esLabels } = usePageModels();

  const { downloadContent, isDownloading, parsedErrorMessage, error } = useDownloadResultContent(
    url || '',
    contentRef,
    prop('download_button_label', labels),
  );

  const linkProps = useMemo(
    () => mapLinkToProps({ href: HREF, openInNewWindow: Boolean(HREF && matchExternalLink(HREF)) }),
    [HREF],
  );

  const {
    queryParams,
    updateQueryParams,
    forceUpdate = () => null,
  } = useContext(ElasticComponentContext);

  const cardCategoriesProps = useCardCategories(
    page,
    esLabels,
    categoryUrl,
    category,
    contentCategory,
    categoryType,
  );

  const handleKeywordClick = (keyword: string) => {
    if (Array.isArray(queryParams.keywords)) {
      updateQueryParams('keywords', [...queryParams.keywords, keyword]);
    } else if (queryParams.keywords?.length > 0) {
      updateQueryParams('keywords', [queryParams.keywords, keyword]);
    } else {
      updateQueryParams('keywords', [keyword]);
    }
  };

  const handleCategoryClick = useCallback(
    ({ e, value, clickType }: { e: React.MouseEvent; value: string; clickType: string }) => {
      e.preventDefault();
      if (!clickType) {
        return;
      }

      const currentValue = queryParams.filters[clickType] || [];
      if (currentValue.includes(value)) {
        return;
      }

      let parsedValue: string[] = [value];

      if (Array.isArray(currentValue)) {
        parsedValue = [...currentValue, value];
      }
      if (currentValue?.length > 0) {
        parsedValue = [...currentValue, value];
      }

      updateQueryParams('page', '1');
      updateQueryParams(clickType, parsedValue, true);
      forceUpdate();

      e.stopPropagation();
    },
    [updateQueryParams, queryParams, forceUpdate],
  );

  const getCategories = useCallback(() => {
    const { categoryProps, categoryWrapped, clickType, chipLabel } = cardCategoriesProps;
    return categoryWrapped.map((item: any) => (
      <Chip
        clickable
        label={chipLabel(item)}
        key={item}
        size="small"
        theme={theme}
        isCategory
        multiple
        chipColor={chipColor}
        className={classes.chip}
        onClick={
          categoryUrl && !categoryUrl[item || contentCategory]
            ? (e: MouseEvent) => handleCategoryClick({ e, value: item, clickType })
            : undefined
        }
        {...categoryProps(item || contentCategory)}
      />
    ));
  }, [
    cardCategoriesProps,
    contentCategory,
    categoryUrl,
    theme,
    chipColor,
    classes.chip,
    handleCategoryClick,
  ]);

  const handleDownloadClick = useCallback(() => {
    downloadContent();
  }, [downloadContent]);

  const rootClassName = clsx(classes.root, `${theme}-theme`, `bg-color-${cardColor}`);

  return (
    <div className={classes.button} ref={contentRef}>
      <MuiCard className={rootClassName} elevation={8} data-testid="qa-card-component">
        <AssetDownloadWrapper
          download={isDownloadable}
          isDownloading={isDownloading}
          error={error}
          errorMessage={parsedErrorMessage}
        >
          <article className={classes.cardContent}>
            {HAS_IMAGE && (
              <div className={classes.image}>
                <img src={image} alt={title} data-testid="qa-card-component-image" />
              </div>
            )}
            <div className={classes.content}>
              <header>
                {(hasCategory || date) && (
                  <div className={classes.header}>
                    {hasCategory && getCategories()}
                    {date && !isPartnerOnly && (
                      <div className={classes.date}>
                        <MuiHidden implementation="css" smUp>
                          <MuiTypography variant="caption" component="span">
                            {date}
                          </MuiTypography>
                        </MuiHidden>
                      </div>
                    )}
                    {isPartnerOnly && (
                      <div>
                        <PartnerOnly blueLabel theme={theme} />
                      </div>
                    )}
                  </div>
                )}
                {title && (
                  <MuiTypography variant="h5" className={classes.title}>
                    {title}
                  </MuiTypography>
                )}
              </header>
              <main className={classes.main}>
                {description && (
                  <RichText
                    className={classes.description}
                    content={
                      description.length > MAX_LENGTH_DESCRIPTION
                        ? `${description.substring(0, MAX_LENGTH_DESCRIPTION)}...`
                        : description
                    }
                  />
                )}
              </main>
              {(tags?.length || date) && (
                <>
                  <div className={classes.spacer} />
                  <footer className={classes.footer}>
                    {tags && (
                      <div className={classes.tagsWrapper}>
                        {tags.length > 0 && (
                          <>
                            <MuiTypography variant="overline" component="span" className="title">
                              {`${labels?.tags || 'TAGS'}`}:
                            </MuiTypography>
                            <div className={classes.tags}>
                              {tags.map((item: any, id) => (
                                <Chip
                                  onClick={() => handleKeywordClick(item)}
                                  label={item}
                                  key={id}
                                  size="small"
                                  chipColor={chipColor}
                                  theme={theme}
                                  multiple={tags.length > 1}
                                  className={classes.chips}
                                />
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                    )}
                    {date &&
                      (isDownloadable ? (
                        <span>{date}</span>
                      ) : (
                        <MuiHidden implementation="css" smDown>
                          <span>{date}</span>
                        </MuiHidden>
                      ))}
                    {isDownloadable && <CardDownloadButton labels={labels} />}
                  </footer>
                </>
              )}
            </div>
          </article>
          <MuiButtonBase
            {...(isDownloadable ? { onClick: handleDownloadClick } : linkProps)}
            aria-label={title}
            className={classes.link}
          />
        </AssetDownloadWrapper>
      </MuiCard>
    </div>
  );
};

export default memo(CardResult);
